import { Navigate, useNavigate } from 'react-router-dom';
import { paths } from './paths';
import { FC, ReactElement, Suspense, useEffect, useState } from 'react';
import { LoggedInContainer } from '@/components/layout/logged-in-container';
import useUserStore from '@/store/useUserStore';
import httpClient from '@/api/httpClient';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Loader } from '@/components/layout/loader';

const ProtectedRoute: FC<{
  children: ReactElement;
  useLayout?: boolean;
}> = ({ children, useLayout = true }) => {
  const { token, clearToken } = useUserStore();
  const navigate = useNavigate();
  const [isTokenValid, setIsTokenValid] = useState(true);

  useEffect(() => {
    const checkTokenValidity = () => {
      if (!token) {
        setIsTokenValid(false);
        return;
      }
      try {
        const decodedToken = jwtDecode<JwtPayload>(token);
        if (typeof decodedToken.exp !== 'number') {
          throw new Error('Token does not contain an expiration claim');
        }
        const expTimestamp = decodedToken.exp * 1000;

        const isValid = Date.now() < expTimestamp;

        setIsTokenValid(isValid);
        if (!isValid) {
          clearToken();
          navigate(paths.LOGIN, { replace: true });
        }
      } catch (error) {
        console.error('Failed to decode token:', error);
        setIsTokenValid(false);
        clearToken();
        navigate(paths.LOGIN, { replace: true });
      }
    };

    checkTokenValidity();
    const intervalId = setInterval(checkTokenValidity, 60000);

    return () => clearInterval(intervalId);
  }, [token, navigate, clearToken]);

  useEffect(() => {
    httpClient.setupResponseInterceptor(navigate);
  }, [navigate]);

  if (!isTokenValid) {
    return <Navigate to={paths.LOGIN} replace />;
  }

  return useLayout ? (
    <Suspense fallback={<Loader />}>
      <LoggedInContainer>{children}</LoggedInContainer>
    </Suspense>
  ) : (
    children
  );
};

export default ProtectedRoute;
